/**
 * NoteAuditDetail Component
 *
 * Detailed view of a single note audit
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate,useParams } from 'react-router-dom';
import { useGetRulesQuery } from '@features/rules/store/api';
import type { ComplianceRuleDocument } from '@features/rules/types';
import {
  ArrowBack as BackIcon,
  Description as NoteIcon,
  OpenInNew as OpenIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Container,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { TRIGGER_LABELS } from '../../constants';
import { useNoteAuditDetail } from '../../hooks/useNoteAuditDetail';
import type { AuditTrigger } from '../../types';
import { RevisionHistory } from '../history/RevisionHistory';
import { DeltaBadge } from '../shared/DeltaBadge';
import { FlagBadge } from '../shared/FlagBadge';
import { QualityScore } from '../shared/QualityScore';

import { CPTReviewPanel } from './CPTReviewPanel';
import { RuleComplianceTable } from './RuleComplianceTable';

/**
 * Format datetime to readable string
 */
const formatDateTime = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

export const NoteAuditDetail: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { noteId } = useParams<{ noteId: string }>();
  const { jotauditDashboard } = useEventTracking();

  const { audit, auditData, isLoading, error, refetch } = useNoteAuditDetail(noteId);

  // Track note detail viewed when data loads
  const viewedTrackedRef = useRef<string | null>(null);
  useEffect(() => {
    if (audit && noteId && viewedTrackedRef.current !== noteId) {
      viewedTrackedRef.current = noteId;
      const revision = audit.current_revision;
      jotauditDashboard.noteDetailViewed({
        note_id: noteId,
        quality_score: revision?.overall_quality_score ?? undefined,
        compliance_percentage: revision?.compliance_percentage ?? undefined,
        total_flags: revision?.total_flags_count ?? undefined,
      });
    }
  }, [audit, noteId, jotauditDashboard]);

  // Fetch all compliance rules
  const { data: rulesData, isLoading: isRulesLoading } = useGetRulesQuery(
    {
      rule_type: 'compliance',
      per_page: 100, // API max is 100
      page: 1,
    },
    { skip: !auditData || auditData.rule_compliance_assessments.length === 0 },
  );

  // Create a map of rule_id -> rule data for quick lookup
  const rulesMap = useMemo(() => {
    if (!rulesData?.items) return new Map<string, ComplianceRuleDocument>();

    const map = new Map<string, ComplianceRuleDocument>();
    rulesData.items.forEach(item => {
      if (item.rule_type === 'compliance' && 'body_md' in item.rule) {
        map.set(item.rule_id, item.rule as ComplianceRuleDocument);
      }
    });
    return map;
  }, [rulesData]);

  const handleBack = () => {
    navigate('/jotaudit?tab=notes');
  };

  const handleOpenNote = () => {
    if (noteId) {
      // Track open note from detail page
      jotauditDashboard.openNoteClicked({
        note_id: noteId,
        source: 'detail_page',
      });
      navigate(`/note/${noteId}`);
    }
  };

  const handleJumpToSection = (sectionId: string) => {
    // This would need integration with the note viewer to scroll to section
    console.log('Jump to section:', sectionId);
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          <AlertTitle>Error loading audit details</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  const noteAudit = audit?.audit;
  const revision = audit?.current_revision;
  const trigger = revision?.trigger as AuditTrigger | undefined;
  const triggerLabel = trigger ? TRIGGER_LABELS[trigger] || trigger : '—';

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Page Header */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={handleBack} sx={{ mr: 0.5 }}>
            <BackIcon />
          </IconButton>

          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <Skeleton variant="rounded" width={48} height={48} />
              <Box>
                <Skeleton width={200} height={28} />
                <Skeleton width={120} height={20} />
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 48,
                  height: 48,
                  borderRadius: 1.5,
                  bgcolor: theme.palette.primary.light + '20',
                  color: theme.palette.primary.main,
                }}
              >
                <NoteIcon />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Note Audit
                </Typography>
                {noteAudit?.note_title && (
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      mt: 0.5,
                    }}
                  >
                    {noteAudit.note_title}
                  </Typography>
                )}
                <Box
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}
                >
                  {audit?.patient_name && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Patient: <strong>{audit.patient_name}</strong>
                    </Typography>
                  )}
                  {audit?.provider_name && (
                    <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                      Provider: <strong>{audit.provider_name}</strong>
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                    {noteAudit ? formatDateTime(noteAudit.last_audit_at) : '—'} • {triggerLabel}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                startIcon={<OpenIcon />}
                onClick={handleOpenNote}
                sx={{ textTransform: 'none' }}
              >
                View Note
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {/* CPT Mismatch Warning */}
      {revision?.cpt_match === false && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          <AlertTitle>CPT Code Mismatch</AlertTitle>
          Provider code
          {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 1 ? 's' : ''} (
          {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 0
            ? revision.provider_cpt_codes.join(', ')
            : '—'}
          ){' '}
          {revision.provider_cpt_codes && revision.provider_cpt_codes.length > 1
            ? 'differ'
            : 'differs'}{' '}
          from AI recommendation
          {(revision?.recommended_cpt_codes?.length ?? 0) > 1 ? 's' : ''} (
          {revision?.recommended_cpt_codes && revision.recommended_cpt_codes.length > 0
            ? revision.recommended_cpt_codes.join(', ')
            : '—'}
          ). Review documentation to ensure proper billing.
        </Alert>
      )}

      {/* Main Content */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 2,
          bgcolor: 'background.paper',
        }}
      >
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* Quality Score */}
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Quality Score
              </Typography>
              {isLoading ? (
                <Skeleton width={60} height={32} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <QualityScore
                    score={revision?.overall_quality_score}
                    variant="badge"
                    size="medium"
                  />
                  {revision?.quality_delta !== null && revision?.quality_delta !== undefined && (
                    <DeltaBadge delta={revision.quality_delta} size="small" />
                  )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Compliance */}
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Compliance
              </Typography>
              {isLoading ? (
                <Skeleton width={60} height={32} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {revision?.compliance_percentage !== null &&
                    revision?.compliance_percentage !== undefined
                      ? `${revision.compliance_percentage.toFixed(0)}%`
                      : '—'}
                  </Typography>
                  {revision?.compliance_delta !== null &&
                    revision?.compliance_delta !== undefined && (
                      <DeltaBadge delta={revision.compliance_delta} label="%" size="small" />
                    )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Total Flags */}
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Total Flags
              </Typography>
              {isLoading ? (
                <Skeleton width={80} height={32} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {revision?.total_flags_count || 0}
                  </Typography>
                  {revision?.flags_delta !== null && revision?.flags_delta !== undefined && (
                    <DeltaBadge delta={revision.flags_delta} positiveIsGood={false} size="small" />
                  )}
                </Box>
              )}
            </Box>
          </Grid>

          {/* Revision */}
          <Grid item xs={6} sm={3}>
            <Box
              sx={{
                p: 2,
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
                bgcolor: theme.palette.grey[50],
              }}
            >
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Revision
              </Typography>
              {isLoading ? (
                <Skeleton width={60} height={32} />
              ) : (
                <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5 }}>
                  {revision?.revision || 1} of {noteAudit?.total_revisions || 1}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Flags & Revision History Row */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* Flags Summary - 50% width */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Flags ({auditData?.flags?.length || 0})
            </Typography>
            {!isLoading && auditData && auditData.flags.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {auditData.flags.map((flag, index) => (
                  <Box
                    key={flag.id || index}
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1.5,
                      p: 1.5,
                      borderRadius: 1,
                      bgcolor: theme.palette.grey[50],
                      border: `1px solid ${theme.palette.grey[200]}`,
                    }}
                  >
                    <FlagBadge priority={flag.priority} compact size="small" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {flag.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: theme.palette.text.secondary, mt: 0.25 }}
                      >
                        {flag.description}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                        <Chip label={flag.impact} size="small" variant="outlined" />
                        {flag.note_section && (
                          <Chip label={flag.note_section} size="small" variant="outlined" />
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                No flags found
              </Typography>
            )}
          </Grid>

          {/* Revision History & Billing Review - 50% width */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Revision History
            </Typography>
            <RevisionHistory noteId={noteId} />

            {/* Billing Review */}
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                Billing Review
              </Typography>
              {isLoading ? (
                <Skeleton variant="rounded" height={200} />
              ) : (
                <CPTReviewPanel
                  recommendedCodes={revision?.recommended_cpt_codes || null}
                  providerCodes={revision?.provider_cpt_codes || null}
                  cptMatch={revision?.cpt_match}
                  billingSupport={auditData?.billing_support || null}
                />
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Rule Compliance Section - Full width with scroll */}
        {!isLoading && auditData && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Rule Compliance
            </Typography>
            <Box
              sx={{
                maxHeight: '80vh',
                overflowY: 'auto',
                border: `1px solid ${theme.palette.grey[200]}`,
                borderRadius: 2,
              }}
            >
              <RuleComplianceTable
                assessments={auditData.rule_compliance_assessments}
                rulesMap={rulesMap}
                isLoadingRules={isRulesLoading}
                onJumpToSection={handleJumpToSection}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default NoteAuditDetail;
